import { FormControl, ValidatorFn } from "@angular/forms";
import { AdditionalTaxInfoValue, FieldPermission } from "app/profile/models/tax.model";
import { isNullOrUndefined } from "app/shared/utils/utils";

/** Field for sensitive information that it could be already stored in the system
 *  and we would only have to show the last 4 digits. Fields like Tax ID, Bank Account Num,
 *  and IBAN number use this approach.
 */
export class MaskedFieldControl {
    /** Full field value of the sensitive field, in case we are editing the field */
    fieldControl: FormControl;

    /** Determines if we are editing the fields to create a new record in the back-end and override the existing one */
    isNewRecord: boolean = true;

    /** Determines if we already have a value of the in the back-end */
    fieldValueExists: boolean = false;

    /** In case we have an existing value for the field, these are the last four digits of it */
    last4Value: string = '';

    constructor(
        initialValue: string,
        private validators: ValidatorFn[],
        isNewRecord?: boolean,
        fieldValueExists?: boolean,
        private originalLast4Value?: string,
    ) {
        this.isNewRecord = isNewRecord === undefined ? true : isNewRecord;
        this.fieldValueExists = fieldValueExists === undefined ? false : fieldValueExists;
        this.last4Value = this.originalLast4Value || '';
        this.fieldControl = new FormControl(initialValue);

        //  If the field value exits, initailize in mask mode, otherwise, initialize in edit mode
        if (this.fieldValueExists) {
            this.maskMode();
        } else {
            this.editMode();
        }
    }

    /** Sets the state to editing the field */
    editMode() {
        this.isNewRecord = true;
        this.fieldValueExists = false;
        this.last4Value = '';

        // Clear value and Set the validators
        this.fieldControl.setValue('', { emitEvent: false });
        this.fieldControl.setValidators(this.validators);
        this.fieldControl.updateValueAndValidity({ emitEvent: false });
    }

    /** Sets the state to not editing the field and use the existing value in the system  */
    maskMode(changeLast4Value: boolean = true, retainValidations: boolean = false) {
        this.isNewRecord = false;
        this.fieldValueExists = true;
        this.last4Value = changeLast4Value ? (this.originalLast4Value || '') : this.last4Value;

        //  Clear validators
        if(!retainValidations)
        {
            this.fieldControl.clearValidators()
            this.fieldControl.updateValueAndValidity({ emitEvent: false });
        }
    }

    /** Sets fields without reseting form control to new instance */
    setFields(
        initialValue: string,
        validators: ValidatorFn[],
        isNewRecord?: boolean,
        fieldValueExists?: boolean,
        last4Value?: string,) {
        this.isNewRecord = isNewRecord === undefined ? true : isNewRecord;
        this.fieldValueExists = fieldValueExists === undefined ? false : fieldValueExists;
        this.last4Value = last4Value || '';
        this.fieldControl.setValue(initialValue, { emitEvent: false });

        //  If the field value exits, initailize in mask mode, otherwise, initialize in edit mode
        if (this.fieldValueExists) {
            this.maskMode(false);
        } else {
            this.editMode();
        }

    }

    setFieldsV2(currentValue: AdditionalTaxInfoValue, permissions: string,  validators: ValidatorFn[])
    {
        this.fieldControl.setValue(currentValue?.taxIdValue, { emitEvent: false });
        
        if(isNullOrUndefined(permissions) || (!isNullOrUndefined(permissions) && permissions?.toLowerCase() === FieldPermission.Read))
        {
            // Mark the field as not editable
            this.fieldControl.disable();
        }

        if(!isNullOrUndefined(permissions) && permissions?.toLowerCase() === FieldPermission.ReadWrite)
        {
            // Mark the field as not editable
            this.fieldControl.enable();
        }

        if(validators?.length > 0)
        {
            this.validators = validators;
            this.fieldControl.setValidators(validators);
        }
        
        this.fieldValueExists = (!isNullOrUndefined(currentValue)) ? true : false;
        this.last4Value = currentValue?.last4TaxIdValue;
        this.maskMode(false);
    }

    clearValidators()
    {
        this.fieldControl.clearValidators();
        this.fieldControl.updateValueAndValidity({ emitEvent: false });
    }

    editModeV2(retainValidations: boolean = false, currentValue: string) {
        this.fieldValueExists = false;
        this.isNewRecord = true;
        this.fieldControl.setValue(currentValue, { emitEvent: false });
        
        if(!retainValidations)
        {
            this.fieldControl.clearValidators();
            this.fieldControl.updateValueAndValidity({ emitEvent: false });
        }
    }
}